import * as z from "zod";

export const minAge = 18;
export const maxAge = 100;

export const calculateAge = (dobTimestamp: number) => {
	const dob = new Date(dobTimestamp);
	const today = new Date();
	const age = today.getFullYear() - dob.getFullYear();
	const monthDiff = today.getMonth() - dob.getMonth();
	const dayDiff = today.getDate() - dob.getDate();

	// Adjust age if birthday hasn't occurred this year yet
	return monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;
};

export const birthDateSchema = z
	.number({ errorMap: () => ({ message: "יש לבחור תאריך לידה" }) })
	.refine(
		(dobTimestamp) => {
			const age = calculateAge(dobTimestamp);
			return age >= 18 && age <= 100;
		},
		{
			message: "יש לבחור תאריך לידה",
		}
	);

export const bituahProviderOptions = [
	"clalit",
	"maccabi",
	"leumit",
	"meuhedet",
	"missing",
	"other",
] as const;

export const sexOptionsLabels = {
	male: "זכר",
	female: "נקבה",
	other: "אחר",
};

export const bituahProviderOptionsLabels: Record<(typeof bituahProviderOptions)[number], string> = {
	clalit: "כללית",
	maccabi: "מכבי",
	leumit: "לאומית",
	meuhedet: "מאוחדת",
	missing: "ללא ביטוח",
	other: "אחר",
};

export const sexOptions = ["male", "female", "other"] as const;

export const NameSchema = z
	.string()
	.min(2, "השדה חייב להכיל לפחות 2 תווים")
	.max(20, { message: "השדה לא יכול להכיל יותר מ 20 תווים" });

export const PhoneSchema = z
	.string({
		errorMap: () => ({ message: "יש להזין מספר טלפון" }),
	})
	.regex(/^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/, "מספר הטלפון לא תקין");

export const UserIdNumberSchema = z
	.string({
		errorMap: () => ({ message: "יש להזין תעודת זהות" }),
	})
	.refine(
		(id) => {
			if (!id || !Number(id) || id.length !== 9 || isNaN(Number(id))) {
				// Make sure ID is formatted properly
				return false;
			}
			let sum = 0,
				incNum;
			for (let i = 0; i < id.length; i++) {
				incNum = Number(id[i]) * ((i % 2) + 1); // Multiply number by 1 or 2
				sum += incNum > 9 ? incNum - 9 : incNum; // Sum the digits up and add to total
			}
			return sum % 10 === 0;
		},
		{
			message: "תעודת זהות לא תקינה",
		}
	);

export const PasswordSchema = z
	.string()
	.min(6, {
		message: "הסיסמא חייבת להכיל לפחות 6 ספרות",
	})
	.max(16, { message: "הסיסמא יכולה להכיל לכל היותר 16 ספרות" });

/**
 * What to call a client on screen when any of the identity fields may be
 * missing. Name first; then the most stable identifier the admin has; then an
 * explicit placeholder — never "undefined undefined" and never an empty string
 * that leaves a heading blank. One helper so every table, header, avatar and
 * email agrees.
 */
export function clientDisplayName(client: {
	firstName?: string | null;
	lastName?: string | null;
	IDNumber?: string | null;
	phone?: string | null;
	email?: string | null;
}): string {
	const name = [client.firstName, client.lastName].filter(Boolean).join(" ").trim();
	return name || client.IDNumber || client.phone || client.email || "לקוח ללא שם";
}

/** Same, but empty when there is no actual name — for places that fall back to
 *  an icon (avatars) or omit the line entirely (task descriptions). */
export function clientFullName(client: { firstName?: string | null; lastName?: string | null }): string {
	return [client.firstName, client.lastName].filter(Boolean).join(" ").trim();
}
